import React, { useState, useEffect, useRef } from 'react';
import { LdSheet } from '../../components/ui/LdSheet';
import { AttestFrage } from '../fehlzeiten/AttestFrage';
import { LdFeld, LdFeldReihe, LdEingabe, LdTextfeld, LdAuswahl, LdDatum, LdZahl, LdUmschalter, LdChips, LdKiHinweis, LdFensterFuss, datumLang } from '../../components/ld/LdFenster';
import { MIcon } from '../../lib/micon';
import { useSchueler } from '../../contexts/SchuelerContext';
import { useIsMobile, useEscapeKey, useOhneMikroKnopf } from '../../lib/hooks';
import { API_BASE, apiHeaders, ApiError, apiGet, apiPost, apiPut, leseFallback, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { TaskErstellenModal } from '../../components/tasks/TaskErstellenModal';
import { NeuerSchuelerAusMailModal, MailVorschlag } from '../../components/schueler/NeuerSchuelerAusMailModal';
import { TerminVorschlagenModal } from '../slot-picking/TerminVorschlagenModal';
import { globalNav, nimmOffeneMailId, nimmMailEntwurf } from '../../lib/nav';
import { KontakteVerwaltung } from './KontakteVerwaltung';
import { AbsenderAvatar, AbsenderBadges, AbsenderKurz, KontaktSheet, PrioStern, Kind } from './MailAbsender';
import { KkMenu, KopfMenuEintrag } from '../klasse/KlassenKopf';
import './mail-k5.css';

// Ordner-Icon + semantische Farbe (Mockup: farbcodierte Ordner-Spalte)
function getFolderIcon(name: string, size = 16): React.ReactNode {
  const n = (name ?? '').toLowerCase();
  if (n.includes('posteingang') || n.includes('inbox')) return <MIcon name="inbox" size={size} />;
  if (n.includes('krank'))       return <MIcon name="thermostat" size={size} color="#DC2626" />;
  if (n.includes('note') || n.includes('auskunft')) return <MIcon name="bar_chart" size={size} color="#6B5FD4" />;
  if (n.includes('eltern') || n.includes('gesprä')) return <MIcon name="group" size={size} color="var(--ld-text-secondary)" />;
  if (n.includes('termin'))      return <MIcon name="event" size={size} color="#14B8A6" />;
  if (n.includes('kollegium') || n.includes('schule')) return <MIcon name="apartment" size={size} />;
  if (n.includes('erledigt') || n.includes('done'))  return <MIcon name="check_circle" size={size} color="#2F8B4E" />;
  if (n.includes('archiv'))      return <MIcon name="archive" size={size} />;
  return <MIcon name="folder" size={size} />;
}

export function MailScreen() {
  const { schuelerListe, setOpenProfilId } = useSchueler();
  const isMobile = useIsMobile();
  // ── Mail-Client States ────────────────────────────────────────────────────
  const [mailFolders,    setMailFolders]    = useState<any[]>([]);
  const [activeFolder,   setActiveFolder]   = useState<number | null>(null);
  const [mailList,       setMailList]       = useState<any[]>([]);
  const [selectedMail,   setSelectedMail]   = useState<any | null>(null);
  useOhneMikroKnopf(isMobile && !!selectedMail); // Welle B1: feste Leiste „Antworten“ unten
  const [mailReplies,    setMailReplies]    = useState<any[]>([]);
  const [mailLoading,    setMailLoading]    = useState(false);
  const [mailSyncing,    setMailSyncing]    = useState(false);
  const [showReplyBox,   setShowReplyBox]   = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveMailId,     setMoveMailId]     = useState<number | null>(null);
  const [aiReply,        setAiReply]        = useState('');
  const [aiReplyLaden,   setAiReplyLaden]   = useState(false);
  const [aiReplyTone,    setAiReplyTone]    = useState('formal');
  const [replyText,      setReplyText]      = useState('');
  const [newFolderName,  setNewFolderName]  = useState('');
  const [gmailVerbunden, setGmailVerbunden] = useState(false);
  // Welle K5: Uhrzeit des letzten Abrufs auf diesem Gerät (nur Anzeige im Kopf)
  const [abgerufen, setAbgerufen] = useState<string | null>(() => { try { return localStorage.getItem('ld-mail-abgerufen'); } catch { return null; } });
  // Krankmeldung → Fehlzeit (2-step modal)
  const [absenceLoading,      setAbsenceLoading]      = useState(false);
  const [showAbsenceModal,    setShowAbsenceModal]    = useState(false);
  const [mailTaskModal,       setMailTaskModal]       = useState<{ subject: string; id: number } | null>(null);
  const [neuerSchuelerData,   setNeuerSchuelerData]   = useState<{ vorschlag: MailVorschlag; klassen: { id: number; name: string }[] } | null>(null);
  const [neuerSchuelerLaden,  setNeuerSchuelerLaden]  = useState(false);
  const [showTerminModal,     setShowTerminModal]     = useState(false);

  async function schuelerAusMail(mailId: number) {
    setNeuerSchuelerLaden(true);
    try {
      const d = await apiPost<any>('/api/schueler/aus-mail-extrahieren', { mail_id: mailId });
      if (d.fehler === 'mail_bereits_zugeordnet') {
        toast(`Mail bereits ${d.schueler?.vorname ?? 'einem Schüler'} ${d.schueler?.nachname ?? ''} zugeordnet`, 'info');
        return;
      }
      setNeuerSchuelerData({ vorschlag: d.vorschlag, klassen: d.klassen ?? [] });
    } catch (e) {
      zeigeApiFehler(e, 'KI-Extraktion fehlgeschlagen');
    } finally {
      setNeuerSchuelerLaden(false);
    }
  }
  const [absencePreview,      setAbsencePreview]      = useState<any>(null);
  const [absenceKandidaten,   setAbsenceKandidaten]   = useState<any[]>([]);
  const [absenceForm,         setAbsenceForm]         = useState<any>({});
  // Terminanfrage → Kalender (2-step modal)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availability,        setAvailability]        = useState<any | null>(null); // Phase B: Terminanfrage-Flow
  const [appointmentLoading,  setAppointmentLoading]  = useState(false);
  const [showSlotModal,       setShowSlotModal]        = useState(false);
  const [availableSlots,      setAvailableSlots]       = useState<any[]>([]);
  const [selectedSlot,        setSelectedSlot]         = useState<any>(null);
  const [apptTeilnehmer,      setApptTeilnehmer]       = useState('');
  const [apptTitel,           setApptTitel]            = useState('');
  const [showReplyDraftModal, setShowReplyDraftModal]  = useState(false);
  const [apptReplyDraft,      setApptReplyDraft]       = useState('');
  const [apptMailId,          setApptMailId]           = useState<number|null>(null);
  // Compose (neue Mail)
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeTo,        setComposeTo]        = useState('');
  const [composeSubject,   setComposeSubject]   = useState('');
  const [composeBody,      setComposeBody]      = useState('');
  const [composeSending,   setComposeSending]   = useState(false);

  // M1b: Filter (über ?filter aus M1a) + Absender speichern/bearbeiten
  const [mailFilter, setMailFilter] = useState<'alle' | 'prio' | 'eltern'>('alle');
  const [kontaktVorlage, setKontaktVorlageRoh] = useState<{ id?: number; email: string; name: string | null; rolle: string | null; prio: 0 | 1 } | null>(null);
  const [kontaktSheetOffen, setKontaktSheetOffen] = useState(false);
  // Teil B: Kontakte verwalten (statt der Mail-Liste) + dezente Hinweise
  const [kontakteAnsicht, setKontakteAnsicht] = useState(false);
  const [kontakteNeuLaden, setKontakteNeuLaden] = useState(0);
  const [mailHinweis, setMailHinweis] = useState<string | null>(null);
  // Vorlage bleibt nach dem Schließen stehen, damit das Sheet sauber zuanimiert
  const setKontaktVorlage = (v: typeof kontaktVorlage) => { if (v) setKontaktVorlageRoh(v); setKontaktSheetOffen(!!v); };

  const [drawerOffen, setDrawerOffen] = useState(false);
  const drawerOffenRef = useRef(false);
  useEffect(() => { drawerOffenRef.current = drawerOffen; }, [drawerOffen]);

  // Smartphone Zurück-Taste schließt Drawer
  useEffect(() => {
    const handlePop = () => {
      if (drawerOffenRef.current) {
        drawerOffenRef.current = false;
        setDrawerOffen(false);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  function oeffneDrawer() {
    window.history.pushState({ ldDrawer: 'mail', ldScreen: 'mails' }, '', window.location.pathname);
    setDrawerOffen(true);
  }

  function schliesseDrawer() {
    drawerOffenRef.current = false;
    setDrawerOffen(false);
    if (window.history.state?.ldDrawer === 'mail') {
      window.history.back();
    }
  }

  // Escape schließt Detail
  useEscapeKey(() => setSelectedMail(null), { seite: true });

  // Ordner + Gmail-Status laden
  useEffect(() => {
    fetchMailFolders();
    fetch(`${API_BASE}/api/gmail/status`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/gmail/status'))
      .then(d => { if (d?.verbunden) setGmailVerbunden(true); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // M1b: aus "Heute" übergebene Mail direkt öffnen (ID wird beim Abholen gelöscht)
  useEffect(() => {
    const id = nimmOffeneMailId();
    if (id !== null) openMail(id);
    // Welle U2: Elternmail aus dem Schülerprofil → Verfassen mit Empfänger
    const entwurf = nimmMailEntwurf();
    if (entwurf) {
      setComposeTo(entwurf.an);
      setComposeSubject(entwurf.betreff);
      setShowComposeModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mails laden wenn Ordner wechselt
  useEffect(() => {
    if (activeFolder !== null) fetchMailsForFolder(activeFolder);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder, mailFilter]);

  async function fetchMailFolders() {
    try {
      const data = await apiGet<any>('/api/mail/folders');
      setMailFolders(data.folders);
      if (activeFolder === null && data.folders.length > 0) setActiveFolder(data.folders[0].id);
    } catch (e) { console.warn('[api] /api/mail/folders:', e); }
  }

  async function fetchMailsForFolder(folderId: number) {
    setMailLoading(true);
    try {
      const d = await apiGet<any>(`/api/mail/folders/${folderId}/messages${mailFilter !== 'alle' ? `?filter=${mailFilter}` : ''}`);
      setMailList(d.messages);
    } catch (e) { console.warn('[api] /api/mail/folders/:id/messages:', e); } finally { setMailLoading(false); }
  }

  async function syncGmail() {
    setMailSyncing(true);
    try {
      await apiPost('/api/mail/sync');
      const uhr = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      setAbgerufen(uhr); try { localStorage.setItem('ld-mail-abgerufen', uhr); } catch { /* egal */ }
      await fetchMailFolders(); if (activeFolder !== null) await fetchMailsForFolder(activeFolder);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setGmailVerbunden(false);
      else zeigeApiFehler(e, 'Synchronisierung fehlgeschlagen');
    } finally { setMailSyncing(false); }
  }

  async function openMail(mailId: number) {
    setAvailability(null); setShowAbsenceModal(false); setShowSlotModal(false);
    try {
      const d = await apiGet<any>(`/api/mail/messages/${mailId}`);
      setSelectedMail(d.mail); setMailReplies(d.replies);
      setShowReplyBox(false); setAiReply(''); setReplyText('');
      setMailList(prev => prev.map(m => m.id === mailId ? { ...m, is_read: 1 } : m));
      fetchMailFolders();
      // Auto-Kategorisierung wenn noch keine KI-Kategorie
      if (!d.mail?.ai_category) {
        apiPost<any>(`/api/mail/messages/${mailId}/categorize`)
          .then(cat => { if (cat) setSelectedMail((prev: any) => prev ? { ...prev, ai_category: cat.ai_category, ai_summary: cat.ai_summary, extracted_data: cat.extracted_data ? JSON.stringify(cat.extracted_data) : null } : prev); })
          .catch(() => { /* Auto-Kategorisierung best-effort */ });
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMailHinweis('Diese Mail ist nicht mehr vorhanden.');
      else console.warn('[api] /api/mail/messages/:id:', e);
    }
  }

  async function categorizeMail(mailId: number) {
    try {
      // apiPost prüft die Kontingent-Warnung selbst (Welle D2)
      const cat = await apiPost<any>(`/api/mail/messages/${mailId}/categorize`);
      setSelectedMail((prev: any) => prev ? { ...prev, ai_category: cat.ai_category, ai_summary: cat.ai_summary, extracted_data: cat.extracted_data ? JSON.stringify(cat.extracted_data) : null } : prev);
    } catch (e) { zeigeApiFehler(e, 'Kategorisierung fehlgeschlagen'); }
  }

  async function openAbsenceModal(mailId: number) {
    setAbsenceLoading(true);
    try {
      const d = await apiPost<any>(`/api/mail/messages/${mailId}/create-absence`, {});
      setAbsencePreview(d.preview);
      setAbsenceKandidaten(d.schueler_kandidaten || []);
      setAbsenceForm({
        schueler_id: d.preview?.schueler_id || '',
        datum: d.preview?.datum || new Date().toISOString().split('T')[0],
        tage: d.preview?.tage ?? 1,
        stunden: d.preview?.stunden ?? '',
        grund: d.preview?.grund || 'Krankmeldung per Mail',
        entschuldigt: d.preview?.entschuldigt !== false,
        attest: false,   // Welle B4: „Liegt ein Attest vor?“ — Vorgabe „Noch nicht“
      });
      setShowAbsenceModal(true);
    } catch (e) { zeigeApiFehler(e); } finally { setAbsenceLoading(false); }
  }

  async function confirmAbsence(mailId: number) {
    setAbsenceLoading(true);
    try {
      const mitPflicht = !!absenceKandidaten.find(k => k.id === absenceForm.schueler_id)?.attestpflicht;
      const d = await apiPost<any>(`/api/mail/messages/${mailId}/create-absence`, { confirmed: true, ...absenceForm, attest: mitPflicht && !!absenceForm.attest });
      const name = d.schueler ? `${d.schueler.vorname} ${d.schueler.nachname}` : 'Schüler/in';
      toast(`Fehlzeit für ${name} eingetragen`, 'success');
      setShowAbsenceModal(false);
      fetchMailFolders();
      if (activeFolder !== null) fetchMailsForFolder(activeFolder);
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Eintragen'); } finally { setAbsenceLoading(false); }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function checkAvailability(mailId: number) { // Phase B: Terminanfrage-Flow
    try {
      setAvailability(await apiPost<any>(`/api/mail/messages/${mailId}/check-availability`));
    } catch (e) { console.warn('[api] check-availability:', e); }
  }

  async function openSlotModal(mailId: number) {
    setAppointmentLoading(true);
    try {
      const d = await apiPost<any>(`/api/mail/messages/${mailId}/check-availability`, {});
      const ext = selectedMail?.extracted_data ? JSON.parse(selectedMail.extracted_data) : {};
      const slots = d.slots || d.free_slots.slice(0,3).map((von: string) => {
        const [h, m] = von.split(':').map(Number);
        const bisH = m >= 30 ? h + 1 : h;
        const bisM = m >= 30 ? 0 : 30;
        return { datum: d.requested_date, von, bis: `${String(bisH).padStart(2,'0')}:${String(bisM).padStart(2,'0')}`, typ: 'Vor Ort' };
      });
      setAvailableSlots(slots);
      setSelectedSlot(null);
      setApptTeilnehmer(ext.parent_name || selectedMail?.sender_name || '');
      setApptTitel('');
      setApptMailId(mailId);
      setShowSlotModal(true);
    } catch (e) { zeigeApiFehler(e, 'Verfügbarkeit konnte nicht geladen werden'); } finally { setAppointmentLoading(false); }
  }

  async function confirmAppointment() {
    if (!selectedSlot || !apptMailId) return;
    setAppointmentLoading(true);
    try {
      const d = await apiPost<any>(`/api/mail/messages/${apptMailId}/create-appointment`,
        { slot: selectedSlot, teilnehmer: apptTeilnehmer, titel: apptTitel });
      setApptReplyDraft(d.antwortvorschlag || '');
      setShowSlotModal(false);
      setShowReplyDraftModal(true);
      fetchMailFolders();
      if (activeFolder !== null) fetchMailsForFolder(activeFolder);
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Erstellen'); } finally { setAppointmentLoading(false); }
  }

  async function sendApptReply() {
    if (!apptMailId || !apptReplyDraft.trim()) { setShowReplyDraftModal(false); return; }
    try {
      await apiPost(`/api/mail/messages/${apptMailId}/send-reply`, { reply_text: apptReplyDraft, tone: 'formal' });
      toast('Antwort gesendet', 'success');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setGmailVerbunden(false);
      zeigeApiFehler(e, 'Senden fehlgeschlagen');
    }
    setShowReplyDraftModal(false);
  }

  async function generateAiReply(tone: string) {
    if (!selectedMail) return;
    setAiReplyLaden(true); setAiReplyTone(tone);
    try {
      // apiPost prüft die Kontingent-Warnung selbst (Welle D2), 429 kommt als ApiError mit Meldung
      const d = await apiPost<any>(`/api/mail/messages/${selectedMail.id}/ai-reply`, { tone });
      setAiReply(d.reply); setReplyText(d.reply);
    } catch (e) { zeigeApiFehler(e, 'KI-Antwort fehlgeschlagen'); } finally { setAiReplyLaden(false); }
  }

  async function sendReply() {
    if (!selectedMail || !replyText.trim()) return;
    try {
      await apiPost(`/api/mail/messages/${selectedMail.id}/send-reply`, { reply_text: replyText, tone: aiReplyTone });
      toast('Antwort gesendet', 'success');
      setMoveMailId(selectedMail.id); setShowMoveDialog(true);
      setShowReplyBox(false); setReplyText(''); setAiReply('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setGmailVerbunden(false);
      zeigeApiFehler(e, 'Senden fehlgeschlagen');
    }
  }

  async function moveMailToFolder(mailId: number, folderId: number) {
    try {
      await apiPut(`/api/mail/messages/${mailId}/move`, { folder_id: folderId });
      setShowMoveDialog(false); setMoveMailId(null); setSelectedMail(null);
      fetchMailFolders(); if (activeFolder !== null) fetchMailsForFolder(activeFolder);
    } catch (e) { zeigeApiFehler(e, 'Verschieben fehlgeschlagen'); }
  }

  async function createFolder() {
    if (!newFolderName.trim()) return;
    try {
      await apiPost('/api/mail/folders', { name: newFolderName.trim() });
      setNewFolderName(''); fetchMailFolders();
    } catch (e) { zeigeApiFehler(e, 'Ordner anlegen fehlgeschlagen'); }
  }

  async function sendCompose() {
    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) return;
    setComposeSending(true);
    try {
      await apiPost('/api/mail/compose', { to: composeTo, subject: composeSubject, body: composeBody });
      toast('Mail gesendet', 'success');
      setShowComposeModal(false);
      setComposeTo(''); setComposeSubject(''); setComposeBody('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setGmailVerbunden(false);
      zeigeApiFehler(e, 'Senden fehlgeschlagen');
    } finally { setComposeSending(false); }
  }

  // Unused ref to satisfy TS (schuelerListe used via bridge only)
  void schuelerListe;

  // ── Render ─────────────────────────────────────────────────────────────────

  // M1b: Datum aus empfangen_am (UTC) in lokaler Zeit; Fallback auf den Header.
  const mailZeitpunkt = (m: any) => new Date(m.empfangen_am || m.date);

  function renderMailZeile(mail: any) {
    return (
      <button key={mail.id} onClick={() => openMail(mail.id)} aria-current={selectedMail?.id === mail.id ? 'true' : undefined}
        className={`ld-mail-row${selectedMail?.id === mail.id ? ' ld-mail-row--selected' : ''}${!mail.is_read ? ' ld-mail-row--unread' : ''}`}>
        <AbsenderAvatar mail={mail} />
        <div className="ld-mail-row__main">
          <div className="ld-mail-row__header">
            {!mail.is_read && <span className="ld-mail-row__dot" role="img" aria-label="ungelesen" />}
            <span className="ld-mail-row__sender">{mail.absender?.kontakt?.name || mail.sender_name || mail.sender_email}</span>
            <PrioStern mail={mail} />
            <span className="ld-mail-row__date">
              {mailZeitpunkt(mail).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </span>
          </div>
          <div className="ld-mail-row__subject">{mail.subject}</div>
          <div className="ld-mail-row__preview">{mail.snippet}</div>
          <AbsenderBadges mail={mail} />
        </div>
      </button>
    );
  }

  // Nach Speichern/Stern: Liste und offene Mail neu laden (ohne erneutes "gelesen").
  async function absenderAktualisieren() {
    if (activeFolder !== null) fetchMailsForFolder(activeFolder);
    if (selectedMail) {
      try {
        const d = await apiGet<any>(`/api/mail/messages/${selectedMail.id}`);
        setSelectedMail((prev: any) => prev && prev.id === d.mail.id ? { ...prev, ...d.mail } : prev);
      } catch (e) { console.warn('[api] /api/mail/messages/:id:', e); }
    }
  }

  async function sternUmschalten(kontaktId: number, an: boolean) {
    try {
      await apiPut(`/api/kontakte/${kontaktId}`, { prio: an });
      toast(an ? 'Kontakt wird als Priorität behandelt' : 'Prio-Stern entfernt', 'success');
      absenderAktualisieren();
    } catch (e) { zeigeApiFehler(e, 'Stern ändern fehlgeschlagen'); }
  }

  function kindOeffnen(k: Kind) {
    setOpenProfilId(k.id);
    globalNav('schueler');
  }

  // ── Welle K5: Ordner im Titel, Mail mit einem nächsten Schritt ──
  const ordner = mailFolders.find(f => f.id === activeFolder);
  const ordnerName = kontakteAnsicht ? 'Kontakte' : (ordner?.name ?? 'Posteingang');
  const ungelesen = ordner?.unread_count ?? 0;
  const kontextZeile = [ungelesen > 0 ? `${ungelesen} ungelesen` : 'alles gelesen', abgerufen ? `abgerufen um ${abgerufen}` : null].filter(Boolean).join(', ');
  const mailDatum = (m: any) => {
    const d = mailZeitpunkt(m);
    return `${d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}, ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  };
  const ttmm = (iso: string) => { const d = new Date(String(iso).slice(0, 10) + 'T12:00:00'); return isNaN(d.getTime()) ? String(iso) : `${d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}, ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}.`.replace('..', '.'); };

  function ordnerWaehlen(id: number) { setActiveFolder(id); setKontakteAnsicht(false); setSelectedMail(null); }

  const listeMenue: KopfMenuEintrag[] = gmailVerbunden ? [{ label: 'Neue Mail schreiben', icon: 'edit_square', onClick: () => setShowComposeModal(true) }] : [];

  const detailMenue = (m: any): KopfMenuEintrag[] => {
    const k = m.absender?.kontakt;
    const e: KopfMenuEintrag[] = [
      { label: 'In Ordner ablegen', icon: 'folder', onClick: () => { setMoveMailId(m.id); setShowMoveDialog(true); } },
      { label: 'Als Aufgabe speichern', icon: 'task_alt', onClick: () => setMailTaskModal({ subject: m.subject, id: m.id }) },
      { label: 'Termin vorschlagen', icon: 'event', onClick: () => setShowTerminModal(true) },
      { label: 'Neuen Schüler aus der Mail anlegen', icon: 'person_add', onClick: () => { if (!neuerSchuelerLaden) schuelerAusMail(m.id); } },
      { label: 'KI-Einschätzung erneuern', icon: 'auto_awesome', onClick: () => categorizeMail(m.id) },
    ];
    if (k) {
      e.push({ label: 'Kontakt bearbeiten', icon: 'contact_page', onClick: () => setKontaktVorlage({ id: k.id, email: m.sender_email, name: k.name, rolle: k.rolle, prio: k.prio }) });
      e.push({ label: k.prio === 1 ? 'Nicht mehr als Priorität' : 'Als Priorität behandeln', icon: 'star', onClick: () => sternUmschalten(k.id, k.prio !== 1) });
    } else if (m.sender_email) {
      e.push({ label: m.absender?.typ ? 'Zusätzlich als Kontakt speichern' : 'Absender speichern', icon: 'contact_page', onClick: () => setKontaktVorlage({ email: m.sender_email, name: m.sender_name ?? null, rolle: null, prio: 0 }) });
    }
    return e;
  };

  const ordnerListe = (imSheet: boolean) => (
    <nav className="ld-ml-ordner" aria-label="Ordner">
      {mailFolders.map(f => (
        <button key={f.id} type="button" className={`ld-ml-ordner__zeile${activeFolder === f.id && !kontakteAnsicht ? ' ist-an' : ''}`}
          aria-current={activeFolder === f.id && !kontakteAnsicht ? 'page' : undefined}
          onClick={() => { ordnerWaehlen(f.id); if (imSheet) schliesseDrawer(); }}>
          <span className="ld-ml-ordner__icon">{getFolderIcon(f.name, 19)}</span>
          <span className="ld-ml-ordner__name">{f.name}</span>
          {f.unread_count > 0 && <span className="ld-ml-ordner__zahl">{f.unread_count}</span>}
        </button>
      ))}
      <button type="button" className={`ld-ml-ordner__zeile ld-ml-ordner__zeile--trenn${kontakteAnsicht ? ' ist-an' : ''}`}
        onClick={() => { setKontakteAnsicht(true); setSelectedMail(null); if (imSheet) schliesseDrawer(); }}>
        <span className="ld-ml-ordner__icon"><MIcon name="contact_page" size={19} /></span>
        <span className="ld-ml-ordner__name">Kontakte</span>
      </button>
      <div className="ld-ml-ordner__neu">
        <LdEingabe aria-label="Neuer Ordner" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Neuer Ordner"
          onKeyDown={e => e.key === 'Enter' && createFolder()}
          rechts={<button type="button" className="ld-fe-icon" aria-label="Ordner anlegen" disabled={!newFolderName.trim()} onClick={createFolder}><MIcon name="add" size={20} /></button>} />
      </div>
    </nav>
  );

  const gmailLeer = (
    <div className="ld-ml-leer">
      <span className="ld-ml-leer__icon" aria-hidden="true"><MIcon name="mark_email_unread" size={26} /></span>
      <b>Gmail verbinden</b>
      <span>Verbinde dein Gmail-Konto, um Mails in LehrerDesk zu lesen und mit KI-Vorschlag zu beantworten.</span>
      <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={async () => {
        try {
          const { url } = await apiGet<{ url: string }>('/api/gmail/auth-url');
          window.location.href = url;
        } catch (e) { zeigeApiFehler(e, 'Gmail-Verbindung fehlgeschlagen'); }
      }}><MIcon name="link" size={18} />Mit Gmail verbinden</button>
    </div>
  );

  return (
    <>
    <div className={`ld-page-enter ld-ml${isMobile ? ' ld-ml--handy' : ''}`}>
      {/* Handy: Kopf mit Ordner im Titel (nur Liste) */}
      {isMobile && !selectedMail && (
        <div className="ld-ml-kopf">
          <div className="ld-ml-kopf__text">
            <button type="button" className="ld-ml-titel" onClick={oeffneDrawer} aria-haspopup="dialog" aria-label={`${ordnerName}, Ordner wählen`}>
              <h1>{ordnerName}</h1><MIcon name="expand_more" size={24} />
            </button>
            {!kontakteAnsicht && <p className="ld-ml-kontext">{kontextZeile}</p>}
          </div>
          <button type="button" className="ld-kk__mehr" onClick={syncGmail} disabled={mailSyncing || !gmailVerbunden} aria-label="Mails abrufen" title={gmailVerbunden ? 'Mails abrufen' : 'Gmail nicht verbunden'}>
            <MIcon name="sync" size={20} className={mailSyncing ? 'ld-mail-spin' : undefined} />
          </button>
          {listeMenue.length > 0 && <KkMenu eintraege={listeMenue} />}
        </div>
      )}

      <div className="ld-ml-rahmen">
        {/* ── PC: Ordner ── */}
        {!isMobile && (
          <aside className="ld-ml-spalte ld-ml-spalte--ordner" aria-label="Ordner">
            <div className="ld-ml-spalte__kopf">
              {gmailVerbunden
                ? <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-ml-neu" onClick={() => setShowComposeModal(true)} aria-label="Neue Mail verfassen"><MIcon name="edit_square" size={18} />Neue Mail</button>
                : <span className="ld-ml-hinweis">Gmail nicht verbunden</span>}
              <button type="button" className="ld-kk__mehr" onClick={syncGmail} disabled={mailSyncing || !gmailVerbunden} aria-label="Mails abrufen" title="Mails abrufen">
                <MIcon name="sync" size={20} className={mailSyncing ? 'ld-mail-spin' : undefined} />
              </button>
            </div>
            {ordnerListe(false)}
          </aside>
        )}

        {/* ── Liste ── */}
        <section className={`ld-ml-spalte ld-ml-spalte--liste${isMobile && selectedMail ? ' ist-versteckt' : ''}`} aria-label="Mails">
          {!isMobile && (
            <div className="ld-ml-spalte__kopf ld-ml-spalte__kopf--liste">
              <h2>{ordnerName}</h2>
              {!kontakteAnsicht && <p className="ld-ml-kontext">{kontextZeile}</p>}
            </div>
          )}
          {mailHinweis && (
            <div className="ld-mabs-info" role="status"><MIcon name="info" size={16} />{mailHinweis}
              <button onClick={() => setMailHinweis(null)} aria-label="Hinweis schließen"><MIcon name="close" size={16} /></button>
            </div>
          )}
          {kontakteAnsicht ? (
            <KontakteVerwaltung gmailVerbunden={gmailVerbunden} neuLaden={kontakteNeuLaden}
              onBearbeiten={k => setKontaktVorlage({ id: k.id, email: k.email, name: k.name, rolle: k.rolle, prio: k.prio })}
              onGeaendert={absenderAktualisieren} onSchliessen={() => setKontakteAnsicht(false)} />
          ) : (<>
            {/* M1b: Filter Alle · Priorität · Eltern */}
            <div className="ld-ml-filter" role="group" aria-label="Mails filtern">
              {([['alle', 'Alle'], ['prio', 'Priorität'], ['eltern', 'Eltern']] as const).map(([wert, label]) => (
                <button key={wert} type="button" onClick={() => setMailFilter(wert)} aria-pressed={mailFilter === wert} className={mailFilter === wert ? 'ist-an' : undefined}>{label}</button>
              ))}
            </div>
            <div className="ld-ml-liste">
              {mailLoading && Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="ld-ml-zeile ld-ml-zeile--laedt">
                  <div className="ld-skeleton ld-skeleton-line ld-skeleton-line--title" style={{ marginBottom: 6 }} />
                  <div className="ld-skeleton ld-skeleton-line" style={{ width: '80%' }} />
                </div>
              ))}
              {!mailLoading && mailList.length === 0 && (
                gmailVerbunden || mailFilter !== 'alle' ? (
                  <div className="ld-ml-leer ld-ml-leer--klein">
                    <b>{mailFilter === 'prio' ? 'Keine Mails mit Priorität' : mailFilter === 'eltern' ? 'Keine Mails von Eltern' : 'Keine Mails'}</b>
                    <span>{mailFilter === 'prio' ? 'Mails von Kontakten mit Stern und dringende Mails erscheinen hier.'
                      : mailFilter === 'eltern' ? 'Mails von hinterlegten Eltern erscheinen hier.' : 'Dieser Ordner ist leer.'}</span>
                  </div>
                ) : gmailLeer
              )}
              {(() => {
                // M1b: Abschnitt "Priorität" — nur Posteingang + Filter "Alle", nur
                // UNGELESENE Mails von Prio-Kontakten aus der geladenen Liste.
                const istPosteingang = /posteingang|inbox/i.test(ordner?.name ?? '');
                const prioOben = istPosteingang && mailFilter === 'alle'
                  ? mailList.filter(m => m.prio_quelle === 'kontakt' && !m.is_read) : [];
                const rest = prioOben.length ? mailList.filter(m => !prioOben.includes(m)) : mailList;
                return (
                  <>
                    {prioOben.length > 0 && <h3 className="ld-ml-abschnitt"><MIcon name="star" size={15} fill />Priorität</h3>}
                    {prioOben.map(renderMailZeile)}
                    {prioOben.length > 0 && rest.length > 0 && <h3 className="ld-ml-abschnitt">Weitere Mails</h3>}
                    {rest.map(renderMailZeile)}
                  </>
                );
              })()}
            </div>
          </>)}
        </section>

        {/* ── Lesebereich ── */}
        <section className={`ld-ml-spalte ld-ml-spalte--detail${isMobile && !selectedMail ? ' ist-versteckt' : ''}`} aria-label="Mail">
          {!selectedMail ? (
            gmailVerbunden ? (
              <div className="ld-ml-leer ld-ml-leer--klein">
                <span className="ld-ml-leer__icon" aria-hidden="true"><MIcon name="mail" size={24} /></span>
                <b>Mail auswählen</b>
                <span>Tippe links auf eine Mail, um sie hier zu lesen.</span>
              </div>
            ) : gmailLeer
          ) : (
            <div className="ld-ml-lesen">
              <div className="ld-ml-lesen__leiste">
                {isMobile
                  ? <button type="button" className="ld-kl-link ld-ml-zurueck" onClick={() => setSelectedMail(null)}><MIcon name="chevron_left" size={20} />{ordnerName}</button>
                  : <span />}
                <KkMenu eintraege={detailMenue(selectedMail)} />
              </div>
              <div className="ld-ml-lesen__inhalt">
                <h1 className="ld-ml-betreff">{selectedMail.subject}</h1>
                <p className="ld-ml-kontext">{mailDatum(selectedMail)}</p>

                {/* M1b: Absender (kompakt, Pfeil zum Profil des Kindes) */}
                <AbsenderKurz mail={selectedMail} onKind={kindOeffnen} />

                {/* ── KI: Krankmeldung ── */}
                {selectedMail.ai_category === 'krankmeldung' && (() => {
                  const d = selectedMail.extracted_data ? JSON.parse(selectedMail.extracted_data) : {};
                  const kind = d.student_name || d.schueler_name; const datum = d.absence_date_from || d.datum; const grund = d.reason || d.grund;
                  return (
                    <div className="ld-ml-ki">
                      <h2><MIcon name="auto_awesome" size={18} />Krankmeldung erkannt</h2>
                      <dl>
                        {kind && <><dt>Kind</dt><dd>{kind}</dd></>}
                        {datum && <><dt>Datum</dt><dd>{ttmm(datum)}</dd></>}
                        {grund && <><dt>Grund</dt><dd>{grund}</dd></>}
                      </dl>
                      <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-ml-ki__haupt" onClick={() => openAbsenceModal(selectedMail.id)} disabled={absenceLoading}>
                        <MIcon name="edit_calendar" size={18} />{absenceLoading ? 'Bitte warten …' : 'Als Fehlzeit eintragen'}
                      </button>
                    </div>
                  );
                })()}

                {/* ── KI: Terminanfrage ── */}
                {(selectedMail.ai_category === 'termin' || selectedMail.vorgeschlagene_aktion === 'termin_anfragen') && (() => {
                  const d = selectedMail.extracted_data ? JSON.parse(selectedMail.extracted_data) : {};
                  return (
                    <div className="ld-ml-ki">
                      <h2><MIcon name="auto_awesome" size={18} />Terminanfrage erkannt</h2>
                      <dl>
                        {d.parent_name && <><dt>Von</dt><dd>{d.parent_name}</dd></>}
                        {d.requested_date && <><dt>Gewünscht</dt><dd>{ttmm(d.requested_date)}{d.requested_time ? `, ${d.requested_time} Uhr` : ''}</dd></>}
                        {d.topic && <><dt>Thema</dt><dd>{d.topic}</dd></>}
                      </dl>
                      <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-ml-ki__haupt" onClick={() => openSlotModal(selectedMail.id)} disabled={appointmentLoading}>
                        <MIcon name="event" size={18} />{appointmentLoading ? 'Bitte warten …' : 'Termin vorschlagen'}
                      </button>
                    </div>
                  );
                })()}

                {/* KI-Zusammenfassung (ohne erkannte Art) */}
                {selectedMail.ai_summary && !selectedMail.ai_category && <LdKiHinweis>{selectedMail.ai_summary}</LdKiHinweis>}

                <div className="ld-ml-text">{selectedMail.body_text || selectedMail.snippet}</div>

                {/* Frühere Antworten */}
                {mailReplies.length > 0 && (
                  <section className="ld-ml-antworten" aria-label="Gesendete Antworten">
                    <h2>Gesendete Antworten</h2>
                    {mailReplies.map((r: any) => (
                      <div key={r.id} className="ld-ml-text ld-ml-text--antwort">
                        <small>{new Date(r.sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}, Ton {r.tone}</small>
                        {r.reply_text}
                      </div>
                    ))}
                  </section>
                )}
              </div>

              {/* Antwort-Leiste: „Antworten“ + rundes Mikro (Handy, statt schwebendem Knopf) */}
              <div className="ld-ml-leiste">
                <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt ld-ml-leiste__antworten" onClick={() => setShowReplyBox(true)}
                  disabled={!selectedMail?.sender_email} title={!selectedMail?.sender_email ? 'Keine Absenderadresse, Antwort nicht möglich' : undefined}>
                  <MIcon name="reply" size={19} />Antworten
                </button>
                {isMobile && (
                  <button type="button" className="ld-ml-leiste__mikro" aria-label="Erzählen"
                    onClick={() => window.dispatchEvent(new CustomEvent('ld-universaldiktat', { detail: { seitenleiste: true } }))}>
                    <MIcon name="mic" size={22} />
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── Antworten (Fenster im K4-Stil, Sende-Weg unverändert) ── */}
      <LdSheet open={showReplyBox && !!selectedMail} onClose={() => setShowReplyBox(false)} maxWidth={620}
        kopf={{ titel: 'Antworten', kontext: selectedMail ? `an ${selectedMail.absender?.kontakt?.name || selectedMail.sender_name || selectedMail.sender_email}` : null, icon: 'reply' }}
        fuss={<LdFensterFuss onAbbrechen={() => { setShowReplyBox(false); setReplyText(''); setAiReply(''); }} haupt="Senden" onHaupt={sendReply}
          aus={!replyText.trim() || !selectedMail?.sender_email} />}>
        <LdFeld label="Vorschlag im Ton">
          <LdChips aria-label="Ton" wert={aiReply ? aiReplyTone : null} onChange={t => { if (!aiReplyLaden) generateAiReply(t); }}
            optionen={(['formal', 'kollegial', 'kurz', 'empathisch'] as const).map(tone => ({
              wert: tone, label: aiReplyLaden && aiReplyTone === tone ? 'Wird geschrieben …' : { formal: 'Formal', kollegial: 'Kollegial', kurz: 'Kurz', empathisch: 'Empathisch' }[tone],
            }))} />
        </LdFeld>
        {aiReply && replyText === aiReply && <LdKiHinweis>Entwurf aus der Mail. Bitte prüfen und anpassen.</LdKiHinweis>}
        <LdFeld label="Antwort">
          <LdTextfeld aria-label="Antwort" rows={8} value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Antworttext" />
        </LdFeld>
      </LdSheet>

      {/* ── Move-Mail Dialog ── */}
      <LdSheet open={showMoveDialog} onClose={() => setShowMoveDialog(false)} title="In Ordner verschieben">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {mailFolders.map(f => (
            <button key={f.id} onClick={() => moveMailToFolder(moveMailId!, f.id)}
              style={{ minHeight: 44, padding: '0 14px', borderRadius: 12, border: '1px solid var(--ld-border-strong)', background: f.id === activeFolder ? 'var(--ld-primary-soft)' : '#fff', color: 'var(--ld-text-primary)', fontSize: 14.5, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'flex' }}>{getFolderIcon(f.name)}</span><span>{f.name}</span>
            </button>
          ))}
        </div>
      </LdSheet>

      {/* Compose-FAB entfernt → Einstieg sitzt jetzt in der Kopfzeile neben „Sync" (mobil + web) */}

      {/* ── Modal: Neue Mail verfassen ── */}
      <LdSheet open={showComposeModal} onClose={() => setShowComposeModal(false)} title="Neue Mail" maxWidth={560}
        fuss={<LdFensterFuss onAbbrechen={() => setShowComposeModal(false)} haupt="Senden" onHaupt={sendCompose} laden={composeSending}
          aus={!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()} />}>
        <LdFeld label="An" htmlFor="ld-compose-to">
          <LdEingabe id="ld-compose-to" type="email" value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="empfaenger@beispiel.de" />
        </LdFeld>
        <LdFeld label="Betreff" htmlFor="ld-compose-subject">
          <LdEingabe id="ld-compose-subject" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Betreff" />
        </LdFeld>
        <LdFeld label="Nachricht" htmlFor="ld-compose-body">
          <LdTextfeld id="ld-compose-body" value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={8} placeholder="Nachricht" />
        </LdFeld>
      </LdSheet>

      {/* ── Modal: Fehlzeit aus Krankmeldung ── */}
      <LdSheet open={showAbsenceModal} onClose={() => setShowAbsenceModal(false)} title="Fehlzeit aus E-Mail" maxWidth={480}
        fuss={absencePreview && selectedMail ? <LdFensterFuss onAbbrechen={() => setShowAbsenceModal(false)} haupt="Fehlzeit eintragen"
          onHaupt={() => confirmAbsence(selectedMail.id)} laden={absenceLoading} aus={!absenceForm.schueler_id} /> : undefined}>
        {absencePreview && selectedMail && (
          <>
            <LdKiHinweis>Aus der Mail ausgefüllt. Bitte kurz prüfen.</LdKiHinweis>
            <LdFeld label="Schüler/in">
              {absenceKandidaten.length > 0 ? (
                <LdAuswahl aria-label="Schüler/in" value={absenceForm.schueler_id || ''} placeholder="Schüler/in wählen"
                  onChange={e => setAbsenceForm((f: any) => ({ ...f, schueler_id: parseInt(e.target.value) || '' }))}>
                  {absenceKandidaten.map(k => <option key={k.id} value={k.id}>{k.vorname} {k.nachname}</option>)}
                </LdAuswahl>
              ) : (
                <p className="ld-fe-fehler">Kein Kind erkannt. Bitte in der Klasse eintragen.</p>
              )}
            </LdFeld>
            <LdFeld label="Datum">
              <LdDatum aria-label="Datum" required value={absenceForm.datum || ''} onChange={e => setAbsenceForm((f: any) => ({ ...f, datum: e.target.value }))} />
            </LdFeld>
            <LdFeldReihe>
              <LdFeld label="Tage">
                <LdZahl aria-label="Tage" value={Number(absenceForm.tage ?? 1)} min={1} max={30} onChange={n => setAbsenceForm((f: any) => ({ ...f, tage: n }))} />
              </LdFeld>
              <LdFeld label="Stunden" optional>
                <LdZahl aria-label="Stunden" value={Number(absenceForm.stunden ?? 0)} min={0} max={10} onChange={n => setAbsenceForm((f: any) => ({ ...f, stunden: n || null }))} />
              </LdFeld>
            </LdFeldReihe>
            <LdFeld label="Grund" optional htmlFor="ld-mail-fz-grund">
              <LdEingabe id="ld-mail-fz-grund" value={absenceForm.grund || ''} onChange={e => setAbsenceForm((f: any) => ({ ...f, grund: e.target.value }))} />
            </LdFeld>
            <LdFeld label="Status">
              <LdUmschalter aria-label="Status" wert={absenceForm.entschuldigt !== false ? 'ja' : 'nein'} onChange={w => setAbsenceForm((f: any) => ({ ...f, entschuldigt: w === 'ja' }))}
                optionen={[{ wert: 'nein', label: 'Unentschuldigt', ton: 'amber' }, { wert: 'ja', label: 'Entschuldigt', ton: 'gruen' }]} />
            </LdFeld>
            {/* Welle B4: Attest gleich mitfragen — nur bei aktiver Attestpflicht des gewählten Kindes */}
            {absenceKandidaten.find(k => k.id === absenceForm.schueler_id)?.attestpflicht && (
              <AttestFrage attest={!!absenceForm.attest} onChange={ja => setAbsenceForm((f: any) => ({ ...f, attest: ja, entschuldigt: ja ? true : f.entschuldigt }))} />
            )}
          </>
        )}
      </LdSheet>

      {/* ── Modal: Slot-Auswahl für Terminanfrage ── */}
      <LdSheet open={showSlotModal} onClose={() => setShowSlotModal(false)} title="Termin auswählen"
        fuss={<LdFensterFuss onAbbrechen={() => setShowSlotModal(false)} haupt="Weiter" onHaupt={confirmAppointment} laden={appointmentLoading} aus={!selectedSlot} />}>
        <LdFeld label="Freier Zeitslot">
          {availableSlots.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {availableSlots.map((slot, i) => {
                const sel = selectedSlot === slot;
                return (
                  <button key={i} type="button" onClick={() => setSelectedSlot(slot)} aria-pressed={sel}
                    style={{ minHeight: 52, padding: '8px 14px', borderRadius: 12, border: `1px solid ${sel ? 'var(--ld-primary)' : 'var(--ld-border-strong)'}`, background: sel ? 'var(--ld-primary-soft)' : '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, font: 'inherit' }}>
                    <span style={{ flex: 1 }}>
                      <b style={{ display: 'block', fontSize: 14.5, color: 'var(--ld-ink)' }}>{datumLang(slot.datum)}</b>
                      <small style={{ fontSize: 13, color: 'var(--ld-text-secondary)' }}>{slot.von}{slot.bis ? ` bis ${slot.bis}` : ''} Uhr, {slot.typ || 'vor Ort'}</small>
                    </span>
                    {sel && <MIcon name="check" size={20} color="var(--ld-primary)" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="ld-fe-text" style={{ color: 'var(--ld-warning-text)' }}>Keine freien Slots am gewünschten Datum gefunden.</p>
          )}
        </LdFeld>
        <LdFeld label="Teilnehmer/in" htmlFor="ld-mail-appt-tn">
          <LdEingabe id="ld-mail-appt-tn" value={apptTeilnehmer} onChange={e => setApptTeilnehmer(e.target.value)} placeholder="Name der Erziehungsberechtigten" />
        </LdFeld>
        <LdFeld label="Titel" optional htmlFor="ld-mail-appt-titel">
          <LdEingabe id="ld-mail-appt-titel" value={apptTitel} onChange={e => setApptTitel(e.target.value)} placeholder="z. B. Elterngespräch" />
        </LdFeld>
      </LdSheet>

      {/* ── Modal: Antwortvorschlag nach Termin ── */}
      <LdSheet open={showReplyDraftModal} onClose={() => setShowReplyDraftModal(false)} title="Antwortvorschlag" maxWidth={560}
        fuss={<LdFensterFuss onAbbrechen={() => { toast('Termin gespeichert, Antwort verworfen', 'success'); setShowReplyDraftModal(false); }} abbrechen="Verwerfen"
          haupt="Antwort senden" onHaupt={sendApptReply}
          links={<button type="button" className="ld-kl-link" onClick={() => { window.location.hash = '#kalender'; setShowReplyDraftModal(false); }}>Zum Kalender</button>} />}>
        <p className="ld-fe-text">Der Termin ist gespeichert. Antwort an die Erziehungsberechtigten, du kannst sie noch ändern:</p>
        <LdKiHinweis>Entwurf aus der Anfrage. Bitte prüfen und anpassen.</LdKiHinweis>
        <LdTextfeld aria-label="Antwort" value={apptReplyDraft} onChange={e => setApptReplyDraft(e.target.value)} rows={8} />
      </LdSheet>
    </div>
    {/* ── M1b: Absender speichern / Kontakt bearbeiten (LdSheet, beide Breakpoints) ── */}
    <KontaktSheet open={kontaktSheetOffen} vorlage={kontaktVorlage}
      onClose={() => setKontaktVorlage(null)} onGespeichert={() => { absenderAktualisieren(); setKontakteNeuLaden(n => n + 1); }} onWiederOeffnen={setKontaktVorlage} />

    {/* ── Handy: Ordnerwahl ── */}
    {isMobile && (
      <LdSheet open={drawerOffen} onClose={schliesseDrawer} maxWidth={480} kopf={{ titel: 'Ordner' }}>
        {ordnerListe(true)}
      </LdSheet>
    )}
    {mailTaskModal && (
      <TaskErstellenModal
        open={!!mailTaskModal}
        onClose={() => setMailTaskModal(null)}
        onErstellt={() => setMailTaskModal(null)}
        initialTitel={mailTaskModal.subject}
        quelle="mail"
        quelle_ref_id={String(mailTaskModal.id)}
        quelle_ref_typ="mail_message"
      />
    )}
    <NeuerSchuelerAusMailModal
      open={!!neuerSchuelerData}
      vorschlag={neuerSchuelerData?.vorschlag ?? null}
      klassen={neuerSchuelerData?.klassen ?? []}
      onClose={() => setNeuerSchuelerData(null)}
      onCreated={() => setNeuerSchuelerData(null)}
    />
    <TerminVorschlagenModal
      open={showTerminModal}
      mailId={selectedMail?.id ?? null}
      onClose={() => setShowTerminModal(false)}
    />
    </>
  );
}
